// Sol/måne-kalkulator for lys-disiplin og operasjonsplanlegging: soloppgang/
// nedgang, borgerlig tussmørke (−6°), BMNT/EENT (nautisk tussmørke −12°, den
// doktrinære definisjonen), solmiddag og månefase/-belysning. Kritisk for
// tidsvindu-planlegging (angrep i tussmørke, NVG-/mørke-vindu).
//
// Ren (avhengighetsfri) implementasjon av den veletablerte sol-/måne-algoritmen
// (samme matematikk som SunCalc / NOAA sunrise equation). Vinkler i radianer
// internt; API tar/returnerer Date + grader.
const rad = Math.PI / 180;
const dayMs = 86_400_000;
const J1970 = 2440588;
const J2000 = 2451545;
const e = rad * 23.4397; // jordaksens skråstilling
function toJulian(date) { return date.valueOf() / dayMs - 0.5 + J1970; }
function fromJulian(j) { return new Date((j + 0.5 - J1970) * dayMs); }
function toDays(date) { return toJulian(date) - J2000; }
function rightAscension(l, b) {
    return Math.atan2(Math.sin(l) * Math.cos(e) - Math.tan(b) * Math.sin(e), Math.cos(l));
}
function declination(l, b) {
    return Math.asin(Math.sin(b) * Math.cos(e) + Math.cos(b) * Math.sin(e) * Math.sin(l));
}
function solarMeanAnomaly(d) { return rad * (357.5291 + 0.98560028 * d); }
function eclipticLongitude(M) {
    const C = rad * (1.9148 * Math.sin(M) + 0.02 * Math.sin(2 * M) + 0.0003 * Math.sin(3 * M));
    const P = rad * 102.9372;
    return M + C + P + Math.PI;
}
function sunCoords(d) {
    const M = solarMeanAnomaly(d);
    const L = eclipticLongitude(M);
    return { dec: declination(L, 0), ra: rightAscension(L, 0) };
}
const J0 = 0.0009;
function julianCycle(d, lw) { return Math.round(d - J0 - lw / (2 * Math.PI)); }
function approxTransit(Ht, lw, n) { return J0 + (Ht + lw) / (2 * Math.PI) + n; }
function solarTransitJ(ds, M, L) {
    return J2000 + ds + 0.0053 * Math.sin(M) - 0.0069 * Math.sin(2 * L);
}
function hourAngle(h, phi, dec) {
    return Math.acos((Math.sin(h) - Math.sin(phi) * Math.sin(dec)) / (Math.cos(phi) * Math.cos(dec)));
}
function getSetJ(h, lw, phi, dec, n, M, L) {
    const w = hourAngle(h, phi, dec);
    const a = approxTransit(w, lw, n);
    return solarTransitJ(a, M, L);
}
/** Sol-tider for en dato og posisjon (UTC-Date-er ut). */
export function sunTimes(date, lat, lon) {
    const lw = rad * -lon;
    const phi = rad * lat;
    const d = toDays(date);
    const n = julianCycle(d, lw);
    const ds = approxTransit(0, lw, n);
    const M = solarMeanAnomaly(ds);
    const L = eclipticLongitude(M);
    const dec = declination(L, 0);
    const Jnoon = solarTransitJ(ds, M, L);
    function pair(hDeg) {
        const Jset = getSetJ(hDeg * rad, lw, phi, dec, n, M, L);
        if (Number.isNaN(Jset))
            return { rise: null, set: null }; // polar dag/natt
        const Jrise = Jnoon - (Jset - Jnoon);
        return { rise: fromJulian(Jrise), set: fromJulian(Jset) };
    }
    const sun = pair(-0.833);
    const civil = pair(-6);
    const naut = pair(-12);
    return {
        solarNoon: fromJulian(Jnoon),
        sunrise: sun.rise, sunset: sun.set,
        dawnCivil: civil.rise, duskCivil: civil.set,
        bmnt: naut.rise, eent: naut.set,
    };
}
/** Dagslengde i timer (sol over −0.833°). 24 ved midnattssol, 0 ved polarnatt. */
export function dayLengthHours(date, lat, lon) {
    const t = sunTimes(date, lat, lon);
    if (t.sunrise && t.sunset)
        return (t.sunset.valueOf() - t.sunrise.valueOf()) / 3_600_000;
    // Ingen opp/nedgang: avgjør om sola er over eller under horisonten ved middag.
    const d = toDays(t.solarNoon);
    const dec = sunCoords(d).dec;
    const alt = Math.asin(Math.sin(rad * lat) * Math.sin(dec) + Math.cos(rad * lat) * Math.cos(dec));
    return alt > -0.833 * rad ? 24 : 0;
}
// ── Måne ────────────────────────────────────────────────────────────────────
function moonCoords(d) {
    const L = rad * (218.316 + 13.176396 * d);
    const M = rad * (134.963 + 13.064993 * d);
    const F = rad * (93.272 + 13.229350 * d);
    const l = L + rad * 6.289 * Math.sin(M);
    const b = rad * 5.128 * Math.sin(F);
    const dt = 385001 - 20905 * Math.cos(M);
    return { ra: rightAscension(l, b), dec: declination(l, b), dist: dt };
}
const PHASE_NAMES = [
    'Nymåne', 'Tiltakende måne', 'Første kvarter', 'Tiltakende måne',
    'Fullmåne', 'Avtakende måne', 'Siste kvarter', 'Avtakende måne',
];
/** Månefase + belysning for en dato. */
export function moonIllumination(date) {
    const d = toDays(date);
    const s = sunCoords(d);
    const m = moonCoords(d);
    const sdist = 149598000; // km sol
    const phi = Math.acos(Math.sin(s.dec) * Math.sin(m.dec) + Math.cos(s.dec) * Math.cos(m.dec) * Math.cos(s.ra - m.ra));
    const inc = Math.atan2(sdist * Math.sin(phi), m.dist - sdist * Math.cos(phi));
    const angle = Math.atan2(Math.cos(s.dec) * Math.sin(s.ra - m.ra), Math.sin(s.dec) * Math.cos(m.dec) - Math.cos(s.dec) * Math.sin(m.dec) * Math.cos(s.ra - m.ra));
    const phase = 0.5 + (0.5 * inc * (angle < 0 ? -1 : 1)) / Math.PI;
    const fraction = (1 + Math.cos(inc)) / 2;
    const name = PHASE_NAMES[Math.round(phase * 8) % 8];
    return { fraction, phase, phaseName: name };
}
//# sourceMappingURL=astro.js.map